// ────────────────────────────────────────────────────────────────────
// "What the history changed" — the explainer panel under the rate strip
// (design_handoff/README.md:177-180).
//
// Two variants, and which one shows is the record's own answer
// (`no_rate_reason`), never a guess made here:
//
//   • history — warm surface, accent micro-label. The asset HAS a measured
//     history, and this panel says what that history did to the date. The
//     sentence is `due.why`, built server-side from the winning candidate's
//     basis and the origins it outranked. Printed verbatim.
//
//   • no-rate — the dashed variant, headed "WHY THIS ASSET HAS NO RATE". An
//     asset with one inspection, no comparable baseline, or a wall already
//     under its minimum has NO corrosion rate, and design_handoff/README.md:319
//     is explicit that it gets this panel rather than an invented number. The
//     interval still on screen is a default, and this panel is where it is
//     called one.
//
// NOTHING ON THIS PANEL IS COMPOSED IN THE BROWSER.
//
// Both paragraphs were, and both quoted the wrong readings. `record.inspections`
// carries the ASSET-WIDE minimum per inspection date; `rules.rate_bases()` and
// the candidate `basis` sentences are written from the GOVERNING ZONE's own
// series, and rate_bases' docstring says why in as many words — quoting the
// asset-wide figure under a number derived from the governing part "would be a
// sentence that names readings the number did not come from".
//
// On an asset whose governing component was not read at every inspection, the
// composed lead announced a span and a count the strip above it contradicted.
// The below-minimum branch was worse: it printed the asset-wide last reading,
// the governing zone's shortfall and the governing minimum in one sentence, and
// on a multi-zone asset those three numbers need not close — a reading ABOVE
// the minimum, described as being below it by a stated amount. Three numbers
// that cannot all be true, on the panel built to make the date arguable.
// ./fixtures multiZoneBelowMinimumRecord() is that asset.
//
// So every sentence here is the server's, printed verbatim: `due.why`,
// `rate_bases.remaining_life` and `rate_bases.governing`. The only prose this
// file still owns is `tail` below, which quotes no number at all.
//
// (The readings themselves are still asked for by DATE and never by position
// — see ./order — wherever a component in this directory needs one at all.)
import type { CSSProperties } from "react";
import type { AssetIntegrity } from "./types";

const LABEL_STYLE: CSSProperties = {
  fontSize: 9.5, letterSpacing: ".1em", color: "var(--accent-deep)",
};

/** The paragraph the no-rate variant leads with: the SERVER's own sentence for
 *  this record's `no_rate_reason`, verbatim.
 *
 *  `rate_bases.governing` is written by the same `no_rate_reason` this panel
 *  branches on (rules.rate_bases:513-522), so the two cannot disagree; the
 *  below-minimum case takes `rate_bases.remaining_life` instead because that is
 *  where the server states the shortfall, against the governing zone's own last
 *  reading. Neither is re-worded here, and no number is quoted that the server
 *  did not put in the sentence. */
function noRateBody(record: AssetIntegrity): string {
  const tail = "The interval still on screen is published as a default and labelled one — it is "
             + "not a measurement, and no rate has been synthesised to stand in for the one this "
             + "asset does not have.";
  const lead = record.no_rate_reason === "below_minimum"
    ? record.rate_bases.remaining_life
    : record.rate_bases.governing;
  return lead ? `${lead}. ${tail}` : tail;
}

export function HistoryExplainer({ record }: { record: AssetIntegrity }): JSX.Element {
  const noRate = record.no_rate_reason !== "";
  const inForce = record.due.in_force;

  return (
    <section
      data-testid="history-explainer"
      data-variant={noRate ? "no-rate" : "history"}
      style={{
        marginTop: 16, padding: "14px 16px", borderRadius: 6,
        // Longhands, not the `border` shorthand: the dashed variant's 1.5px is
        // part of the design (README.md:179) and a shorthand carrying a var()
        // is the shape jsdom cannot read back at all.
        borderWidth: noRate ? "1.5px" : "1px",
        borderStyle: noRate ? "dashed" : "solid",
        borderColor: noRate ? "var(--hairline)" : "var(--line-2)",
        // The handoff's two warm panel washes (README.md:177-180), taken from
        // the palette tokens rather than its hex literals — every colour in this
        // app arrives through :root (styles/HARDCODED-COLOURS.md).
        background: noRate ? "var(--surface-warm-3)" : "var(--surface-warm)",
      }}
    >
      <div className="font-mono" style={LABEL_STYLE}>
        {noRate ? "WHY THIS ASSET HAS NO RATE" : "WHAT THE HISTORY CHANGED"}
      </div>

      <p data-testid="explainer-body"
         style={{ fontSize: 11.5, lineHeight: 1.65, color: "var(--ink-2)", marginTop: 7 }}>
        {/* Verbatim on both branches. The history variant leads with where the
            remaining life came from — the reading, the minimum, the part and the
            rate — which is the server's own sentence about what the measured
            history did, quoted from the series the number was measured on. */}
        {noRate ? noRateBody(record) : record.rate_bases.remaining_life}
      </p>

      {!noRate && (
        <p data-testid="explainer-why"
           style={{ fontSize: 11.5, lineHeight: 1.65, color: "var(--ink-1)", marginTop: 6 }}>
          {/* Verbatim: the server wrote this sentence to be printed. */}
          {record.due.why}
        </p>
      )}

      {noRate && inForce && (
        <p data-testid="explainer-default"
           style={{ fontSize: 10.5, lineHeight: 1.55, color: "var(--ink-3)", marginTop: 6 }}>
          {inForce.basis}
        </p>
      )}
    </section>
  );
}
